import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing } from "lucide-react";
import { api } from "@/lib/api";

const BUMP = { PAID: "Start Cooking", PREPARING: "Mark Ready", READY: "Done ✓" };
const CARD_STYLE = {
  PAID: "border-gold shadow-gold/20",
  PREPARING: "border-emerald-500 shadow-emerald-500/10",
  READY: "border-grape shadow-grape/20",
};
const TAG_STYLE = {
  PAID: "bg-gold text-chalk",
  PREPARING: "bg-emerald-500 text-white",
  READY: "bg-grape text-cream",
};
const TAG_LABEL = { PAID: "NEW ORDER", PREPARING: "COOKING", READY: "READY" };
const NEXT = { PAID: "PREPARING", PREPARING: "READY", READY: "COMPLETED" };

export default function Kds() {
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    api.get("/kds/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearInterval(t);
      clearInterval(tick);
    };
  }, [load]);

  const bump = async (id) => {
    setOrders((os) =>
      os
        .map((o) => (o.id === id ? { ...o, status: NEXT[o.status] } : o))
        .filter((o) => o.status !== "COMPLETED")
    );
    await api.post(`/kds/orders/${id}/advance`).catch(load);
  };

  const elapsed = (iso) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));

  return (
    <div data-testid="kds-page" className="min-h-screen bg-chalk px-4 py-6 font-body text-cream">
      <header className="mx-auto flex max-w-6xl items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-10 w-10 rounded-full" />
          <div>
            <h1 className="font-display text-2xl font-bold">Kitchen Display</h1>
            <p className="text-xs text-cream/40">Auto-refreshes every 8 seconds</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-cream/5 px-4 py-2 text-sm font-bold text-gold">
          <BellRing className="h-4 w-4" />
          <span data-testid="kds-count">{orders.filter((o) => o.status !== "READY").length} active</span>
        </div>
      </header>

      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <p data-testid="kds-empty" className="font-editorial text-2xl italic text-cream/40">
            All caught up — no orders in the queue
          </p>
        </div>
      ) : (
        <div className="mx-auto mt-6 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {orders.map((o) => {
              const mins = elapsed(o.created_at);
              return (
                <motion.div
                  key={o.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  data-testid={`kds-order-${o.id}`}
                  className={`rounded-3xl border-4 bg-deep p-5 shadow-xl ${CARD_STYLE[o.status]}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-wider ${TAG_STYLE[o.status]}`}>
                      {TAG_LABEL[o.status]}
                    </span>
                    <span className={`font-display text-lg font-bold ${mins >= 20 ? "text-red-400" : "text-cream/50"}`}>
                      {mins}m
                    </span>
                  </div>
                  <p className="mt-3 font-display text-2xl font-bold">{o.customer?.name}</p>
                  <p className="text-xs text-cream/50">Pickup: {o.pickup_time}</p>
                  <ul className="mt-3 space-y-1.5 border-t border-dashed border-cream/15 pt-3">
                    {o.items.map((it) => (
                      <li key={it.item_id} className="flex gap-2 text-base">
                        <span className="font-display font-bold text-gold">{it.qty}×</span>
                        <span>
                          {it.title}
                          {it.build && (
                            <span className="block text-xs leading-snug text-cream/60">{it.build}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`kds-bump-${o.id}`}
                    onClick={() => bump(o.id)}
                    className={`mt-4 w-full rounded-full py-3.5 font-display text-lg font-semibold transition-all active:scale-95 ${
                      o.status === "READY"
                        ? "bg-cream/10 text-cream hover:bg-cream/20"
                        : "bg-gold text-chalk hover:brightness-110"
                    }`}
                  >
                    {BUMP[o.status]}
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
